import { Badge, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";

interface RoleBadgeProps {
  role?: 'user' | 'pro' | 'admin';
  planId?: 'free' | 'pro' | 'admin';
  isEditable?: boolean;
  onRoleChange?: (newRole: 'user' | 'pro' | 'admin') => Promise<void>;
}

export default function RoleBadge({
  role,
  planId,
  isEditable = false,
  onRoleChange
}: RoleBadgeProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'user' | 'pro' | 'admin'>(role || 'user');
  const [isLoading, setIsLoading] = useState(false);

  const getRoleVariant = (roleToCheck: string) => {
    switch (roleToCheck) {
      case 'admin':
        return 'danger';
      case 'pro':
        return 'warning';
      case 'user':
      default:
        return 'primary';
    }
  };

  const getRoleText = (roleToCheck: string) => {
    switch (roleToCheck) {
      case 'admin':
        return 'Admin';
      case 'pro':
        return 'Pro';
      case 'user':
      default:
        return 'Free';
    }
  };

  const handleEditClick = () => {
    setSelectedRole(role || 'user');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (onRoleChange && selectedRole !== role) {
      setIsLoading(true);
      try {
        await onRoleChange(selectedRole);
      } catch (error) {
        console.error('Failed to update role:', error);
        // Revert to original role on error
        setSelectedRole(role || 'user');
      } finally {
        setIsLoading(false);
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setSelectedRole(role || 'user');
    setIsEditing(false);
  };

  const displayRole = role || 'user';
  const displayText = getRoleText(displayRole);
  const variant = getRoleVariant(displayRole);

  if (isEditing) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Form.Select
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value as 'user' | 'pro' | 'admin')}
          disabled={isLoading}
          size="sm"
          style={{ width: 'auto' }}
        >
          <option value="user">Free</option>
          <option value="pro">Pro</option>
          <option value="admin">Admin</option>
        </Form.Select>
        <FontAwesomeIcon
          icon={faCheck}
          color="green"
          cursor="pointer"
          onClick={handleSave}
          title="Save role change"
          style={{ opacity: isLoading ? 0.5 : 1 }}
        />
        <FontAwesomeIcon
          icon={faTimes}
          color="red"
          cursor="pointer"
          onClick={handleCancel}
          title="Cancel"
        />
      </div>
    );
  }

  return (
    <div className="d-inline-flex align-items-center gap-2">
      <Badge bg={variant}>{displayText}</Badge>
      {isEditable && (
        <FontAwesomeIcon
          icon={faEdit}
          color="white"
          size="sm"
          cursor="pointer"
          onClick={handleEditClick}
          title="Edit role"
        />
      )}
    </div>
  );
}